"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  FORMULA_ERROR_STRINGS,
  type FormulaErrorStrings,
  FormulaView,
  formulaErrorMessage,
  WORKING_BOX,
} from "@/components/tools/chemistry/formula-view";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  formatFixed,
  formatPlain,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";
import {
  chargeText,
  type FormulaError,
  formulaToUnicode,
  type MolarMass,
  molarMass,
  type ParsedFormula,
  parseFormula,
  partMolarMass,
  partToUnicode,
} from "@/lib/tool-kit/chemistry/formula";

/** English strings. A translated content file overrides these through `ui`. */
export const MOLAR_MASS_CALCULATOR_STRINGS = {
  formulaLabel: "Chemical formula",
  formulaHint:
    "Case matters: Co is cobalt, CO is carbon monoxide. Join a hydrate with · or *, and write a charge as Fe^3+ or SO4^2-.",
  formulaPlaceholder: "C6H12O6",
  tryLabel: "Try an example",
  decimals: "Round the molar mass to",
  decimalPlaces: "one: {count} decimal place|other: {count} decimal places",
  results: "Results",
  molarMass: "Molar mass",
  atoms: "Atoms per formula unit",
  charge: "Charge",
  neutral: "Neutral",
  formulaShown: "Formula",
  perElement: "Mass of each element",
  columnElement: "Element",
  columnAtoms: "Atoms",
  columnWeight: "Atomic weight (g/mol)",
  columnSubtotal: "Subtotal (g/mol)",
  columnPercent: "Mass %",
  parts: "Parts of the formula",
  columnPart: "Part",
  columnCoefficient: "Coefficient",
  columnPartMass: "Molar mass (g/mol)",
  columnContribution: "Contribution (g/mol)",
  columnShare: "Share of total",
  working: "The working",
  sumLine: "Σ = {sum} = {total} g/mol",
  hydrateLine: "{coefficient} × {mass} = {total} g/mol",
  copy: "Copy molar mass",
  copied: "Copied!",
  copyText: "{formula}: {mass} g/mol",
  massNumberNote:
    "{elements} has no IUPAC standard atomic weight, so its mass number (the longest-lived isotope) is used. Treat the result as approximate.",
  chargeNote:
    "The charge is shown but does not change the molar mass: the electrons gained or lost weigh less than the uncertainty of most atomic weights.",
  formulaErrors: FORMULA_ERROR_STRINGS,
} as const;

const EXAMPLES = [
  "H2O",
  "C6H12O6",
  "Ca(OH)2",
  "Al2(SO4)3",
  "CuSO4·5H2O",
  "NH4+",
] as const;
const DECIMAL_CHOICES = ["2", "3", "4"] as const;
const PERCENT_PLACES = 2;
const UNIT = "g/mol";

type Strings = typeof MOLAR_MASS_CALCULATOR_STRINGS;

function ElementTable({
  mass,
  strings,
  fmt,
}: {
  mass: MolarMass;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  return (
    <DataTable
      head={[
        strings.columnElement,
        strings.columnAtoms,
        strings.columnWeight,
        strings.columnSubtotal,
        strings.columnPercent,
      ]}
      rows={mass.rows.map((row) => [
        `${row.name} (${row.symbol})`,
        formatPlain(fmt, row.count),
        formatPlain(fmt, row.weight),
        formatPlain(fmt, row.mass),
        `${formatFixed(fmt, row.fraction * 100, PERCENT_PLACES)}%`,
      ])}
    />
  );
}

function PartsTable({
  formula,
  total,
  strings,
  fmt,
}: {
  formula: ParsedFormula;
  total: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const rows = formula.parts.map((part) => {
    const each = partMolarMass(part);
    const contribution = each * part.coefficient;
    return [
      partToUnicode(part),
      formatPlain(fmt, part.coefficient),
      formatPlain(fmt, each),
      formatPlain(fmt, contribution),
      `${formatFixed(fmt, (contribution / total) * 100, PERCENT_PLACES)}%`,
    ];
  });
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.parts}</h3>
      <DataTable
        head={[
          strings.columnPart,
          strings.columnCoefficient,
          strings.columnPartMass,
          strings.columnContribution,
          strings.columnShare,
        ]}
        rows={rows}
      />
    </div>
  );
}

function Working({
  mass,
  decimals,
  strings,
  fmt,
}: {
  mass: MolarMass;
  decimals: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const terms = mass.rows
    .map(
      (row) =>
        `${formatPlain(fmt, row.count)} × ${formatPlain(fmt, row.weight)}`
    )
    .join(" + ");
  const sum = mass.rows.map((row) => formatPlain(fmt, row.mass)).join(" + ");
  return (
    <div className={WORKING_BOX}>
      <p className="font-medium font-sans text-foreground">{strings.working}</p>
      <p>{terms}</p>
      <p>
        {t(strings.sumLine, {
          sum,
          total: formatFixed(fmt, mass.total, decimals),
        })}
      </p>
    </div>
  );
}

function FormulaProblem({
  error,
  messages,
}: {
  error: FormulaError;
  messages: FormulaErrorStrings;
}) {
  const strings = useToolUi(MOLAR_MASS_CALCULATOR_STRINGS);
  if (error.code === "empty") {
    return <Notice>{strings.formulaErrors.empty}</Notice>;
  }
  return <ErrorNotice>{formulaErrorMessage(messages, error)}</ErrorNotice>;
}

function MassResults({
  formula,
  decimals,
}: {
  formula: ParsedFormula;
  decimals: number;
}) {
  const strings = useToolUi(MOLAR_MASS_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const mass = molarMass(formula);
  const total = formatFixed(fmt, mass.total, decimals);
  const approximate = mass.rows
    .filter((row) => row.massNumber)
    .map((row) => row.symbol)
    .join(", ");
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.molarMass}
          value={`${total} ${UNIT}`}
        />
        <StatCard
          label={strings.formulaShown}
          value={<FormulaView formula={formula} />}
        />
        <StatCard label={strings.atoms} value={formatPlain(fmt, mass.atoms)} />
        {formula.charge === 0 ? null : (
          <StatCard label={strings.charge} value={chargeText(formula.charge)} />
        )}
      </StatGrid>
      {mass.usesMassNumber ? (
        <Notice>{t(strings.massNumberNote, { elements: approximate })}</Notice>
      ) : null}
      {formula.charge === 0 ? null : <Notice>{strings.chargeNote}</Notice>}
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.perElement}</h3>
        <ElementTable fmt={fmt} mass={mass} strings={strings} />
      </div>
      {formula.parts.length > 1 ? (
        <PartsTable
          fmt={fmt}
          formula={formula}
          strings={strings}
          total={mass.total}
        />
      ) : null}
      <Working decimals={decimals} fmt={fmt} mass={mass} strings={strings} />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          formula: formulaToUnicode(formula),
          mass: total,
        })}
      />
    </>
  );
}

export default function MolarMassCalculatorWidget() {
  const strings = useToolUi(MOLAR_MASS_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("C6H12O6");
  const [decimals, setDecimals] =
    useState<(typeof DECIMAL_CHOICES)[number]>("3");
  const parsed = parseFormula(text);

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
        <TextField
          hint={strings.formulaHint}
          id="mm-formula"
          label={strings.formulaLabel}
          mono
          onChange={setText}
          placeholder={strings.formulaPlaceholder}
          value={text}
        />
        <SelectField
          id="mm-decimals"
          label={strings.decimals}
          onChange={setDecimals}
          options={DECIMAL_CHOICES.map((value) => ({
            value,
            label: tPlural(strings.decimalPlaces, locale, Number(value), {
              count: fmt(Number(value)),
            }).trim(),
          }))}
          value={decimals}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.tryLabel}</span>
        {EXAMPLES.map((example) => (
          <button
            className={BUTTON_SMALL}
            key={example}
            onClick={() => setText(example)}
            type="button"
          >
            {example}
          </button>
        ))}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {parsed.ok ? (
          <MassResults decimals={Number(decimals)} formula={parsed.formula} />
        ) : (
          <FormulaProblem
            error={parsed.error}
            messages={strings.formulaErrors}
          />
        )}
      </section>
    </>
  );
}
